import { createId } from '@paralleldrive/cuid2';
import type { BrandInsightsPageContext, Chat, ChatContextItem } from '@trinity/chat';
import {
  ChatContextType,
  isBrandInsightsContext,
  isEverythingContext,
  KolSearchStatusEnum,
  ChatDataSourcesMap,
  ChatSource,
  ChatSourcesMap,
  isBrandContext,
  isFolderContext,
  isProjectContext,
} from '@trinity/chat';
import type { ChatSessionState, ChatState, SettingsState } from './interfaces';
import type { ChatSessionManagerState } from './interfaces.chat-session-manager';

type GenerateEmptyChatSessionStateData = {
  context?: BrandInsightsPageContext.Item | null;
  contextItems: ChatContextItem[];
  features: {
    hcpProfiles: boolean;
    workspaces: boolean;
    engagementFilter: boolean;
  };
  brandIdentifier: string;
  pendingQuery?: string | null;
  landingCardsVisible: boolean;
};

export function generateIntialKolProfileContext(): BrandInsightsPageContext.KolProfile {
  return { type: ChatContextType.KolProfile, data: { id: null }, metadata: { name: '' } };
}

export function buildDefaultChatContext(contextType: ChatContextType): BrandInsightsPageContext.Item {
  switch (contextType) {
    case ChatContextType.KolProfile:
      return generateIntialKolProfileContext();

    case ChatContextType.Global:
      return { type: ChatContextType.Global };

    default:
      return null;
  }
}

export function generateEmptyChatSessionState({
  context,
  features,
  brandIdentifier,
  pendingQuery,
  landingCardsVisible,
}: GenerateEmptyChatSessionStateData): ChatSessionState.State {
  if (!context) {
    throw new Error('Context is required to generate empty chat session state');
  }

  const resolvedContext = context ?? buildDefaultChatContext(ChatContextType.Global);

  return {
    chatInstance: {
      createdOn: new Date(),
      id: null,
      identifier: createId(),
      name: '',
      currentQueryIdentifier: null,
      context: resolvedContext,
      shared: null,
      bookmarked: false,
      filterOverrides: {},
      brandIdentifier: brandIdentifier,
    },
    input: '',
    selectedQueryIdentifier: null,
    queries: {},
    filters: generateEmptyChatSessionFiltersState({ context: resolvedContext, features }),
    fileUploads: [],
    pendingFindQuotes: false,
    kolSearches: [],
    pendingQuery: pendingQuery ?? null,
    pendingHintPromptIdentifier: null,
    landingCardsVisible,
  };
}

export function generateEmptyKolSearch(): Chat.KolSearch {
  return {
    id: null,
    createdOn: new Date(),
    status: KolSearchStatusEnum.ProfileRecommendations,
    identifier: createId(),
    followupQuestions: [],
    form: {
      name: '',
    },
    results: null,
    profile: null,
    recommendations: {
      status: 'uninitialized',
      items: [],
      total: 0,
    },
    citations: [],
  };
}

type GenerateEmptyChatSessionFiltersState = {
  context?: BrandInsightsPageContext.Item;
  features: {
    hcpProfiles: boolean;
    workspaces: boolean;
    engagementFilter: boolean;
  };
};

export function generateEmptyChatSessionFiltersState({
  context,
  features,
}: GenerateEmptyChatSessionFiltersState): ChatSessionState.State['filters'] {
  if (!context) {
    return {
      custom: [],
      dataSources: [],
      sources: [],
      minDocumentDate: null,
      tags: [],
      projects: [],
    };
  }

  const kolDataSources = [].map((m) => ({
    id: m,
    name: ChatDataSourcesMap.get(m),
  }));

  const sources = getChatSourceFilterOptions({ context, features });

  const kolEngagement = features.engagementFilter
    ? { id: 'proactive', name: 'Proactive' }
    : { id: 'all', name: 'All (Reactive and Proactive)' };

  const tags = context.immutableFilters?.tags ?? [];

  switch (context.type) {
    case ChatContextType.KolProfile:
      return {
        custom: [],
        dataSources: kolDataSources,
        sources,
        minDocumentDate: null,
        tags,
        engagement: kolEngagement,
        projects: [],
      };

    case ChatContextType.Global:
      return {
        custom: [],
        dataSources: [...(features.hcpProfiles ? kolDataSources : [])].filter(Boolean),
        sources,
        minDocumentDate: null,
        tags,
        engagement: kolEngagement,
        projects: [],
      };

    default:
      return {
        custom: [],
        dataSources: [],
        sources,
        minDocumentDate: null,
        tags,
        projects: [],
      };
  }
}

export function generateEmptyChatSessionManagerState(): ChatSessionManagerState.State {
  return {
    currentChatInstanceIdentifier: null,
    nextChatInstanceIdentifier: null,
    status: 'uninitialized',
  };
}

type GenerateEmptyChatStateData = {
  settings: SettingsState.State;
} & GenerateEmptyChatSessionStateData;

export function generateEmptyChatState(data: GenerateEmptyChatStateData): ChatState.State {
  const context = data.context || data.settings.defaultContext;
  return {
    contextItems: data.contextItems,
    settings: data.settings,
    history: {
      manager: {
        mode: 'page',
        cursor: null,
        loading: false,
        hasMore: false,
        initialized: false,
      },
      items: [],
      shared: [],
      tabs: {
        index: 0,
        items: [],
      },
    },
    session: generateEmptyChatSessionState({
      context,
      features: data.features,
      brandIdentifier: data.brandIdentifier,
      contextItems: data.contextItems,
      landingCardsVisible: data.landingCardsVisible,
    }),
    sessionManager: generateEmptyChatSessionManagerState(),
  };
}

export function getChatSourceFilterOptions({ context, features }: GenerateEmptyChatSessionFiltersState) {
  const sources: ChatSource[] = [];

  const hcpSources = [ChatSource.Profiles, ChatSource.MslInteractions];

  if (context.type === ChatContextType.KolProfile) {
    sources.push(...hcpSources);
  }
  if (context.type === ChatContextType.Global && features.hcpProfiles) {
    sources.push(...hcpSources);
  }

  const workspaceSources = [
    ChatSource.Presentations,
    ChatSource.Text,
    ChatSource.Transcripts,
    ChatSource.Dashboards,
  ];

  if (context.type === ChatContextType.Global && features.workspaces) {
    sources.push(...workspaceSources);
  }

  if (isBrandContext(context)) {
    sources.push(...workspaceSources);
  }
  if (isFolderContext(context)) {
    sources.push(...workspaceSources);
  }
  if (isEverythingContext(context) && isBrandInsightsContext(context)) {
    sources.push(...workspaceSources);
  }

  if (isProjectContext(context)) {
    sources.push(...workspaceSources);
    sources.push(ChatSource.SurveyAnalyses);

    // Survey is only exists in InsightsEDGE (brand-insights), not in the GenAI Platform
    if (context != null && isBrandInsightsContext(context)) {
      sources.push(ChatSource.Surveys);
    }
  }

  return sources.map((s) => ({
    id: s,
    name: ChatSourcesMap.get(s),
  }));
}
